import React, { useEffect, useMemo, useState } from "react";
import { gql } from "@apollo/client";
import {
  useGetGoogleBusinessAccountsQuery,
  useCreateGoogleBusinessGuestFeedbackIntegrationMutation,
} from "./GoogleBusinessAccountDialog.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@src/lib/ui/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { GraphqlOperations } from "@src/types.generated";

type Props = {
  oAuthId: string;
  onClose: () => void;
};

const GoogleBusinessAccountDialog: React.FC<Props> = ({ oAuthId, onClose }) => {
  const { addToast, addErrorToast } = useToast();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );

  const { data, loading } = useGetGoogleBusinessAccountsQuery({
    variables: { oauthId: oAuthId },
  });

  const [createIntegration, { loading: creating }] =
    useCreateGoogleBusinessGuestFeedbackIntegrationMutation({
      refetchQueries: [
        GraphqlOperations.Query.OrgGuestFeedbackIntegrations,
        GraphqlOperations.Query.GuestFeedbackSetupStatus,
      ],
      onCompleted: (result) => {
        if (result.createGoogleBusinessGuestFeedbackIntegration.success) {
          addToast({ message: "Google Business integration created" });
          onClose();
        } else {
          addErrorToast({
            message:
              result.createGoogleBusinessGuestFeedbackIntegration.error
                ?.message ?? "Failed to create integration",
            callsite: "google_business_integration_create",
          });
        }
      },
    });

  const accounts = useMemo(
    () => data?.getGoogleBusinessAccounts ?? [],
    [data?.getGoogleBusinessAccounts],
  );
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Auto-submit if there's only one account, otherwise select first by default
  useEffect(() => {
    if (accounts.length === 1 && !autoSubmitted && !creating) {
      setAutoSubmitted(true);
      createIntegration({
        variables: {
          oAuthId: oAuthId,
          googleBusinessAccountId: accounts[0].id,
          googleBusinessAccountName: accounts[0].name,
        },
      });
    } else if (accounts.length > 1 && selectedAccountId === null) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [
    accounts,
    selectedAccountId,
    autoSubmitted,
    creating,
    createIntegration,
    oAuthId,
  ]);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  const handleSubmit = () => {
    if (!selectedAccountId || !selectedAccount) return;
    createIntegration({
      variables: {
        oAuthId: oAuthId,
        googleBusinessAccountId: selectedAccountId,
        googleBusinessAccountName: selectedAccount.name,
      },
    });
  };

  // Don't render dialog if loading, auto-submitting single account, or creating
  if (loading || accounts.length === 1) {
    return null;
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Google Account</DialogTitle>
          <DialogDescription>
            Choose which Google account to connect for guest feedback
            integration.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col gap-2 py-4">
            <Skeleton className="h-9 w-full" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="py-4 text-muted-foreground">
            No Google accounts found.
          </div>
        ) : (
          <div className="py-4">
            <Select
              value={selectedAccountId ?? undefined}
              onValueChange={setSelectedAccountId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedAccountId || loading || creating}
          >
            {creating ? "Creating..." : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleBusinessAccountDialog;

gql`
  query GetGoogleBusinessAccounts($oauthId: UUID!) {
    getGoogleBusinessAccounts(oauthId: $oauthId) {
      id
      name
    }
  }

  mutation CreateGoogleBusinessGuestFeedbackIntegration(
    $oAuthId: UUID!
    $googleBusinessAccountId: String!
    $googleBusinessAccountName: String!
  ) {
    createGoogleBusinessGuestFeedbackIntegration(
      oAuthId: $oAuthId
      googleBusinessAccountId: $googleBusinessAccountId
      googleBusinessAccountName: $googleBusinessAccountName
    ) {
      success
      error {
        message
      }
      orgGuestFeedbackIntegration {
        id
      }
    }
  }
`;
